import React from "react";

/**
 * Input component with primary color border on focus and smooth animation.
 * Follows UI guidelines: rounded-xl, professional, no shadow.
 * @see .cursor/ui.md
 */
const Input = ({
  label,
  value,
  placeholder,
  onChange,
  onBlur,
  onFocus,
  type = "text",
  variant = "default",
  className = "",
  disabled = false,
  error = false,
  success = false,
  required = false,
  size = "default", // "default" | "sm" | "lg"
  icon: Icon,
  iconPosition = "left",
  ...props
}) => {
  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    default: "px-4 py-3 text-sm",
    lg: "px-4 py-3.5 text-base",
  };

  const baseClasses =
    "w-full bg-white border text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 ease-out focus:outline-none";

  const stateClasses = error
    ? "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
    : success
    ? "border-green-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
    : "border-gray-300 hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20";

  const variantClasses = {
    default: "rounded-xl",
    edit: "rounded-xl bg-gray-50 focus:bg-white",
    placeholder: "rounded-xl bg-gray-50 text-gray-400 focus:bg-white",
  };

  const inputClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    variantClasses[variant] || variantClasses.default,
    disabled && "bg-gray-50 text-gray-500 cursor-not-allowed",
    Icon && (iconPosition === "left" ? "pl-11" : "pr-11"),
  ]
    .filter(Boolean)
    .join(" ");

  const labelClasses =
    "block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-200";

  return (
    <div className={className}>
      {(variant === "default" || variant === "edit") && label && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative">
        {Icon && iconPosition === "left" && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none flex items-center">
            <Icon className="w-5 h-5 shrink-0" />
          </div>
        )}

        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          className={inputClasses}
          {...props}
        />

        {Icon && iconPosition === "right" && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none flex items-center">
            <Icon className="w-5 h-5 shrink-0" />
          </div>
        )}
      </div>

      {typeof error === "string" && (
        <p className="mt-1.5 text-xs font-medium text-red-600 flex items-center gap-1 animate-fadeIn">
          {error}
        </p>
      )}
    </div>
  );
};

export default Input;
